// Account · Security — self-service password change for the signed-in employee.
//
// Lifted out of My Space: a password form has no business sitting between someone's
// address and their leave calendar, and it made an otherwise read-mostly profile
// page feel like a settings screen. Reached from the "You" group in the sidebar.
//
// It is a real form on the shared fields now (SH-14). It had two inputs named
// only by their placeholders, outside any <form>: Enter did nothing, a password
// manager could not pair them, and it carried a third private copy of the
// password box, without the focus ring the other two had.
import { useEffect, useState, type FormEvent } from 'react';
import type { OAuthGrant } from '@supabase/supabase-js';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { fmtDate } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { Button } from '../components/primitives';
import { SensitiveMark } from '../components/people';
import { Card } from '../components/card';
import { useToast } from '../components/feedback';
import { useBelow } from '../lib/useMediaQuery';
import {
  Field,
  MIN_PASSWORD,
  PwToggle,
  passwordProblem,
  type PasswordProblem,
} from '../components/auth-ui';

const copy = {
  margin: 0,
  fontSize: 'var(--hf-text-sm)',
  lineHeight: 'var(--hf-leading-body)',
  color: 'var(--hf-ink-3)',
} as const;

// Apps connected through MCP (ConsentScreen). Not shown when there are none, or
// when the OAuth server is off and GoTrue has no grants to list.
function ConnectedApps() {
  const toast = useToast();
  const { dateFormat, tz } = useOrgDates();
  const [grants, setGrants] = useState<OAuthGrant[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let live = true;
    void supabase?.auth.oauth.listGrants().then(({ data }) => live && setGrants(data ?? []));
    return () => {
      live = false;
    };
  }, [reload]);

  const disconnect = async (g: OAuthGrant) => {
    if (!supabase || busy) return;
    setBusy(g.client.id);
    const { error } = await supabase.auth.oauth.revokeGrant({ clientId: g.client.id });
    setBusy(null);
    if (error)
      return toast.push({ tone: 'error', message: `Couldn’t disconnect ${g.client.name}.` });
    toast.push({ tone: 'success', message: `${g.client.name} is disconnected.` });
    setReload((n) => n + 1);
  };

  if (!grants.length) return null;
  return (
    <Card label="Connected apps" right={<SensitiveMark label="Only you" />}>
      <p style={copy}>
        These apps read Hyprflow as you: read-only, with no pay or project ₹. Disconnecting signs
        them out; connect again from the app to use one.
      </p>
      <ul style={{ listStyle: 'none', margin: 'var(--hf-space-3) 0 0', padding: 0 }}>
        {grants.map((g) => (
          <li
            key={g.client.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--hf-space-3)',
              padding: 'var(--hf-space-2) 0',
              borderTop: '1px solid var(--hf-rule)',
            }}
          >
            <span style={{ color: 'var(--hf-ink)' }}>
              {g.client.name}
              <span style={{ ...copy, display: 'block' }}>
                Connected {fmtDate(g.granted_at, dateFormat, { tz })}
              </span>
            </span>
            <Button size="sm" loading={busy === g.client.id} onClick={() => void disconnect(g)}>
              Disconnect
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function SecurityScreen() {
  const { session, updatePassword } = useAuth();
  const toast = useToast();
  const phone = useBelow('md');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasswordProblem | null>(null);
  const email = session?.user.email;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const problem = passwordProblem(pw, confirm);
    setError(problem);
    if (problem) return;
    setBusy(true);
    const { error } = await updatePassword(pw);
    setBusy(false);
    // The server's complaint is about the new password ("should be different
    // from the old one"), so it goes under that field.
    if (error) return setError({ on: 'password', text: error });
    // Cleared, so the new password isn't left sitting in the DOM.
    setPw('');
    setConfirm('');
    toast.push({ tone: 'success', message: 'Password updated. Use it next time you sign in.' });
  };

  const errorOn = (on: PasswordProblem['on']) => (error?.on === on ? error.text : undefined);
  const edit = (set: (v: string) => void) => (v: string) => {
    set(v);
    if (error) setError(null);
  };

  return (
    <div
      style={{
        // Left-aligned, at the gutter every other screen has: centred, it was
        // the one screen whose cards didn't start under the title (critique
        // 2026-10-06, minor). 640 stays the measure: a password form is narrow.
        padding: phone ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        maxWidth: 640,
      }}
    >
      <Card label="Password" right={<SensitiveMark label="Only you" />}>
        <form
          onSubmit={save}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}
        >
          <p style={copy}>
            Set a new password — at least {MIN_PASSWORD} characters. You’ll use it next time you
            sign in
            {email ? ' as ' : ''}
            {email && (
              <span style={{ color: 'var(--hf-ink-2)', fontFamily: 'var(--hf-font-mono)' }}>
                {email}
              </span>
            )}
            .
          </p>
          {/* Not shown: it tells a password manager which account the new
              password belongs to, so it updates the saved one. */}
          <input type="email" autoComplete="username" value={email ?? ''} readOnly hidden />
          <Field
            label="New password"
            type={shown ? 'text' : 'password'}
            value={pw}
            onChange={edit(setPw)}
            placeholder={`At least ${MIN_PASSWORD} characters`}
            autoComplete="new-password"
            error={errorOn('password')}
            required
            trailing={<PwToggle shown={shown} onClick={() => setShown((s) => !s)} />}
          />
          <Field
            label="Confirm new password"
            type={shown ? 'text' : 'password'}
            value={confirm}
            onChange={edit(setConfirm)}
            placeholder="The same password again"
            autoComplete="new-password"
            error={errorOn('confirm')}
            required
          />
          <div>
            <Button variant="primary" type="submit" loading={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </Card>

      {/* Describes the flow, not a guarantee. This said "a password on its own is
          not enough to reach your account", which is false: the emailed code is
          checked only by this app's sign-in screen, nothing server-side requires
          it (no aal2), and a password alone yields a full API session. Don't
          promise a second factor here until the server enforces one. */}
      <ConnectedApps />

      <Card label="How sign-in works">
        <p style={copy}>
          Signing in to the app asks for your password and a six-digit code emailed to you. Accounts
          are created by your HR team — ask an administrator if you need access changed.
        </p>
      </Card>
    </div>
  );
}
